import { useState, useEffect, useCallback, useRef, createContext, useContext } from "react";
import type { AuthUser } from "../lib/auth";
import { getCurrentAuthUser, login, logout, completeNewPassword, isAdmin, isEmployee } from "../lib/auth";
import { roleApi } from "../lib/roleApi";
import { bizApi } from "../lib/bizApi";
import type { PageKey, ApprovalScope, ApprovalAction } from "../types/pages";
import type { MembershipInfo, BusinessWithAdminFlag } from "../types/index";

// Admin pages that a business admin can access (scoped to their business via /biz/{id}/... APIs).
const BIZ_ADMIN_PAGES: PageKey[] = [
  "admin:dashboard",
  "admin:users",
  "admin:loans",
  "admin:leave",
  "admin:tasks",
  "admin:attendance",
  "admin:reports",
  "store:manage-store",
];

const ACTIVE_BIZ_KEY = "bgh_active_business_id";

export interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  needsPasswordChange: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  changePassword: (newPassword: string) => Promise<void>;
  isAdmin: boolean;
  isEmployee: boolean;
  // "*" = super-admin (all access), array = list of permitted page keys for normal_user.
  // Flat union across all businesses — used as a fallback when no business is active.
  permittedPages: PageKey[] | "*";
  // Per-business page access, keyed by businessId. Drives hasPageAccess for the active business.
  pagesByBusiness: Record<string, PageKey[]>;
  roleId: string | null;
  hasPageAccess: (page: PageKey) => boolean;
  /**
   * Whether the current user may perform an approval action in the active business.
   * super-admin and business admins always can; custom-role members are gated by
   * the per-user restrictions set on the Manage Approvals page. UI-only — the
   * backend enforces the same rule.
   */
  canApproveAction: (scope: ApprovalScope, action: ApprovalAction) => boolean;
  /** Page set for a specific business (used to compute the landing route on switch). */
  pagesForBusiness: (businessId: string) => PageKey[] | "*";
  refreshPermissions: () => Promise<void>;
  // Multi-business context
  memberships: MembershipInfo[];
  businesses: BusinessWithAdminFlag[];
  activeBusinessId: string | null;
  setActiveBusiness: (businessId: string) => void;
  isBusinessAdmin: boolean;        // true if user is a business admin in ANY active membership
  isActiveBusinessAdmin: boolean;  // true if user is a business admin in the currently selected business
  isSuperAdmin: boolean;
}

export const AuthContext = createContext<AuthState | null>(null);

async function loadPermissions(user: AuthUser | null): Promise<{
  permittedPages: PageKey[] | "*";
  pagesByBusiness: Record<string, PageKey[]>;
  roleId: string | null;
  memberships: MembershipInfo[];
}> {
  if (!user) return { permittedPages: [], pagesByBusiness: {}, roleId: null, memberships: [] };

  // super-admin always has full access — skip the API call.
  if (user.groups.includes("super-admin")) {
    return { permittedPages: "*", pagesByBusiness: {}, roleId: null, memberships: [] };
  }

  try {
    const res = await roleApi.getMyPermissions();
    const memberships = res.memberships ?? [];
    const isAnyBizAdmin = memberships.some(
      (m) => m.isBusinessAdmin && m.status === "ACTIVE",
    );

    let permittedPages: PageKey[] | "*" = res.permittedPages;

    // Flat fallback (used when no business is active): if the user is a business admin
    // anywhere, grant the admin page set. Per-business gating is handled separately in
    // hasPageAccess via pagesByBusiness. Backend enforces business-scoping on the data.
    if (isAnyBizAdmin && permittedPages !== "*") {
      const merged = Array.from(new Set([
        ...(permittedPages as PageKey[]),
        ...BIZ_ADMIN_PAGES,
      ]));
      permittedPages = merged as PageKey[];
    }

    return { permittedPages, pagesByBusiness: res.pagesByBusiness ?? {}, roleId: res.roleId, memberships };
  } catch {
    // Fail closed: grant no pages rather than all pages on API error.
    return { permittedPages: [], pagesByBusiness: {}, roleId: null, memberships: [] };
  }
}

async function loadBusinesses(
  user: AuthUser | null,
): Promise<BusinessWithAdminFlag[]> {
  if (!user) return [];
  try {
    const res = await bizApi.myBusinesses();
    return res.items;
  } catch {
    return [];
  }
}

function resolveActiveBusinessId(
  businesses: BusinessWithAdminFlag[],
  stored: string | null,
): string | null {
  if (!businesses.length) return null;
  if (stored && businesses.some((b) => b.businessId === stored)) return stored;
  // Default to the first admin business, then any business.
  const adminBiz = businesses.find((b) => b.isBusinessAdmin);
  return (adminBiz ?? businesses[0]).businessId;
}

export function useAuthState(): AuthState {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const [permittedPages, setPermittedPages] = useState<PageKey[] | "*">([]);
  const [pagesByBusiness, setPagesByBusiness] = useState<Record<string, PageKey[]>>({});
  const [roleId, setRoleId] = useState<string | null>(null);
  const [memberships, setMemberships] = useState<MembershipInfo[]>([]);
  const [businesses, setBusinesses] = useState<BusinessWithAdminFlag[]>([]);
  const [activeBusinessId, setActiveBusinessIdState] = useState<string | null>(null);

  // `silent` refreshes permissions/businesses in the background without flipping the
  // fullscreen loading state — used for the tab-refocus refresh so returning to the
  // app doesn't blank the screen. Permission/business data still updates, so the nav,
  // business switcher, and route guards recompute (and drop access the user lost).
  const applyUser = useCallback(async (u: AuthUser | null, opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setUser(u);
    try {
      const [perms, bizs] = await Promise.all([
        loadPermissions(u),
        loadBusinesses(u),
      ]);
      setPermittedPages(perms.permittedPages);
      setPagesByBusiness(perms.pagesByBusiness);
      setRoleId(perms.roleId);
      setMemberships(perms.memberships);
      setBusinesses(bizs);

      const stored = localStorage.getItem(ACTIVE_BIZ_KEY);
      const resolved = resolveActiveBusinessId(bizs, stored);
      setActiveBusinessIdState(resolved);
      if (resolved) localStorage.setItem(ACTIVE_BIZ_KEY, resolved);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    getCurrentAuthUser().then(applyUser);
  }, [applyUser]);

  const signIn = useCallback(async (username: string, password: string) => {
    const result = await login(username, password);
    if (result.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
      setNeedsPasswordChange(true);
      return;
    }
    const u = await getCurrentAuthUser();
    await applyUser(u);
  }, [applyUser]);

  const signOut = useCallback(async () => {
    await logout();
    setUser(null);
    setPermittedPages([]);
    setPagesByBusiness({});
    setRoleId(null);
    setMemberships([]);
    setBusinesses([]);
    setActiveBusinessIdState(null);
    setNeedsPasswordChange(false);
    localStorage.removeItem(ACTIVE_BIZ_KEY);
  }, []);

  const changePassword = useCallback(async (newPassword: string) => {
    await completeNewPassword(newPassword);
    const u = await getCurrentAuthUser();
    await applyUser(u);
    setNeedsPasswordChange(false);
  }, [applyUser]);

  const isSuperAdmin = user?.groups.includes("super-admin") ?? false;

  const isBusinessAdminAny = memberships.some(
    (m) => m.isBusinessAdmin && m.status === "ACTIVE",
  );

  // True when the current view should use /biz/{activeBusinessId}/... routes.
  // Super-admins are always biz-scoped once they have an active business selected
  // (backend bypasses the membership check for them). Non-super-admin users must
  // have an explicit isBusinessAdmin membership for the active business.
  const isActiveBusinessAdmin =
    activeBusinessId !== null &&
    (isSuperAdmin ||
      memberships.some(
        (m) =>
          m.businessId === activeBusinessId &&
          m.isBusinessAdmin &&
          m.status === "ACTIVE",
      ));

  const hasPageAccess = useCallback((page: PageKey): boolean => {
    if (permittedPages === "*") return true; // super-admin

    // Gate by the active business when one is selected, so switching businesses
    // changes which pages are visible.
    if (activeBusinessId) {
      const bizPages = pagesByBusiness[activeBusinessId] ?? [];
      if (bizPages.includes(page)) return true;
      // Business admins of the active business get the admin page set (UI-only;
      // backend enforces business-scoping via the isBusinessAdmin bypass).
      if (isActiveBusinessAdmin && BIZ_ADMIN_PAGES.includes(page)) return true;
      return false;
    }

    // No active business (e.g. landing page) → fall back to the flat union.
    return permittedPages.includes(page);
  }, [permittedPages, pagesByBusiness, activeBusinessId, isActiveBusinessAdmin]);

  const canApproveAction = useCallback((scope: ApprovalScope, action: ApprovalAction): boolean => {
    if (permittedPages === "*") return true; // super-admin
    if (isActiveBusinessAdmin) return true;  // biz admins keep full approval rights
    if (!activeBusinessId) return true;      // no business context → don't block (backend enforces)
    const m = memberships.find(
      (x) => x.businessId === activeBusinessId && x.status === "ACTIVE",
    );
    return !m?.approvalDenies?.[scope]?.includes(action);
  }, [permittedPages, isActiveBusinessAdmin, activeBusinessId, memberships]);

  // Resolve the page set for a SPECIFIC business (used when switching businesses,
  // before activeBusinessId has updated). "*" for super-admin.
  const pagesForBusiness = useCallback((businessId: string): PageKey[] | "*" => {
    if (permittedPages === "*") return "*";
    const base = pagesByBusiness[businessId] ?? [];
    const isAdminOfBiz = memberships.some(
      (m) => m.businessId === businessId && m.isBusinessAdmin && m.status === "ACTIVE",
    );
    if (isAdminOfBiz) return Array.from(new Set([...base, ...BIZ_ADMIN_PAGES]));
    return base;
  }, [permittedPages, pagesByBusiness, memberships]);

  const refreshPermissions = useCallback(() => applyUser(user), [applyUser, user]);

  // Refresh permissions when the user returns to this tab, throttled to at most
  // once every 2 minutes so we don't hammer the API on rapid tab switches.
  const lastRefreshRef = useRef<number>(0);
  useEffect(() => {
    if (!user) return;
    const THROTTLE_MS = 2 * 60 * 1000;
    const maybeRefresh = () => {
      const now = Date.now();
      if (now - lastRefreshRef.current >= THROTTLE_MS) {
        lastRefreshRef.current = now;
        // Silent: update permissions/businesses in the background without the
        // fullscreen spinner (avoids the white flash on tab return).
        applyUser(user, { silent: true });
      }
    };
    const onVisibility = () => { if (document.visibilityState === "visible") maybeRefresh(); };
    const onFocus = () => maybeRefresh();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [user, applyUser]);

  const setActiveBusiness = useCallback((businessId: string) => {
    setActiveBusinessIdState(businessId);
    localStorage.setItem(ACTIVE_BIZ_KEY, businessId);
  }, []);

  return {
    user,
    loading,
    needsPasswordChange,
    signIn,
    signOut,
    changePassword,
    isAdmin: isAdmin(user),
    isEmployee: isEmployee(user),
    permittedPages,
    pagesByBusiness,
    roleId,
    hasPageAccess,
    canApproveAction,
    pagesForBusiness,
    refreshPermissions,
    memberships,
    businesses,
    activeBusinessId,
    setActiveBusiness,
    isBusinessAdmin: isBusinessAdminAny,
    isActiveBusinessAdmin,
    isSuperAdmin,
  };
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
